import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CoverPageMarketingStrategy from './CoverPageMarketingStrategy';

const useStyles = makeStyles(() => ({
  page: {
    background: '#fff',
    padding: 0,
  },
  exportRoot: {
    width: '100%',
    margin: '0 auto',
    background: '#fff',
    padding: 0,
    boxSizing: 'border-box',
    fontFamily: 'Inter, "Helvetica Neue", Arial, sans-serif',
    color: '#111827',
    '--paper-w': '8.5in',
    '--paper-h': '11in',
    '--paper-m': '0.5in',
    '--report-page-w': 'calc(var(--paper-w) - (2 * var(--paper-m)))',
    '--report-page-h': 'calc(var(--paper-h) - (2 * var(--paper-m)))',
  },
  reportPage: {
    width: 'var(--report-page-w)',
    minWidth: 'var(--report-page-w)',
    maxWidth: 'var(--report-page-w)',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  reportSection: {
    padding: '0.16in 0.32in 0.18in',
    background: '#fff',
  },
  section: {
    pageBreakInside: 'avoid',
    breakInside: 'avoid-page',
    marginBottom: '6px',
    overflow: 'visible',
  },
  '@global': {
    '@page': { size: 'var(--paper-w) var(--paper-h)', margin: 'var(--paper-m)' },
    '@media print': {
      'html, body': {
        background: '#fff !important',
        margin: '0 !important',
        padding: '0 !important',
        color: '#111827 !important',
      },
      '#root': { background: '#fff !important' },
      '*': { WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' },
      '.bsv-report-export, .bsv-report-export *': {
        transition: 'none !important',
        animation: 'none !important',
        '-webkit-font-smoothing': 'antialiased',
        textRendering: 'geometricPrecision',
      },
      '.bsv-report-export, .bsv-report-export #report-root, .bsv-report-export .reportPage': {
        background: '#fff !important',
        color: '#111827 !important',
      },
      '.bsv-report-export .theme-dark, .bsv-report-export [data-theme="dark"]': {
        background: '#fff !important',
        color: '#111827 !important',
      },
      '.Cmt-sidebar, .Cmt-header, [data-staging-banner="true"]': { display: 'none !important' },
      '.Cmt-appMainContainer, .Cmt-appMain': { margin: '0 !important', width: '100% !important', padding: '0 !important' },
      '#report-root *': { boxSizing: 'border-box' },
      '#report-root canvas, #report-root svg': { maxWidth: '100% !important' },
      '#report-root': {
        width: '100% !important',
        maxWidth: '100% !important',
        padding: '0 !important',
        margin: '0 auto !important',
        background: '#fff !important',
      },
      '[data-report-page="true"]:empty': { display: 'none !important' },
      '[data-report-chart-grid="true"]': {
        display: 'grid !important',
        gridTemplateColumns: '1fr !important',
        columnGap: '14px !important',
        rowGap: '10px !important',
      },
      '[data-report-chart-item="true"]': {
        breakInside: 'avoid-page !important',
        pageBreakInside: 'avoid !important',
      },
      '[data-keep-with-chart="true"]': {
        breakInside: 'avoid-page !important',
        pageBreakInside: 'avoid !important',
      },
      '.report-range-chart [data-range-marker-label="up"]': {
        top: '-30px !important',
      },
      '.report-range-chart [data-range-marker-label="down"]': {
        top: '14px !important',
      },
      '.report-range-chart [data-range-middle-line="true"]': {
        backgroundColor: '#6B7280 !important',
      },
      '.report-range-chart [data-range-label-lane="true"]': {
        height: '28px !important',
      },
      '[data-report-legend="true"], [data-report-legend="true"] *': {
        color: '#111827 !important',
      },
      '.bsv-report-export .report-gauge-chart': {
        width: '370px !important',
        height: '300px !important',
        margin: '0 auto !important',
        overflow: 'visible !important',
      },
      /* Custom section START */
      '.bsv-report-export [data-hide-item="true"]': {
        display: 'none !important',
      },
      '.bsv-report-export [data-color-black="true"]': {
        color: 'black !important',
      },
      '.bsv-report-export [data-remove-margin="true"]': {
        margin: '0 !important',
      },
      '.bsv-report-export [data-remove-padding="true"]': {
        padding: '0 !important',
      },
      '.bsv-report-export [data-negative-top-margin="true"]': {
        marginTop: '-50px !important',
      },
      '.bsv-report-export [data-remove-gap="true"]': {
        gap: '0 !important',
      },
      '.bsv-report-export [data-brand-color-text="true"]': {
        color: '#19989B !important',
      },
      '.bsv-report-export [data-consistency-container="true"]': {
        width: '45% !important',
      },
      '.bsv-report-export [data-page-break="true"]': {
        pageBreakAfter: 'always',
        breakAfter: 'page',
      },
      '.bsv-report-export [data-campaign-impact-width="true"]': {
        width: '300px !important',
      },
      '.bsv-report-export [data-campaign-impact-margin="true"]': {
        marginRight: '20px !important',
      },
      '.bsv-report-export [data-campaign-impact-gauge-chart-margin="true"]': {
        marginLeft: '-70px !important',
      },
      '.bsv-report-export [data-campaign-impact-gauge-chart-font-size="true"]': {
        fontSize: '14px !important',
      },

      /* Custom section END */

      '.bsv-report-export .report-gauge-chart > div': {
        width: '370px !important',
        height: '300px !important',
      },
      '.bsv-report-export .report-gauge-chart-total': {
        width: '430px !important',
        height: '330px !important',
      },
      '.bsv-report-export .report-gauge-chart-total > div': {
        width: '430px !important',
        height: '330px !important',
      },
      '.bsv-report-export .report-gauge-chart canvas, .bsv-report-export .report-gauge-chart svg': {
        display: 'block !important',
        margin: '0 auto !important',
      },
      '.bsv-report-export [data-report-subtitle="true"]': {
        width: '82% !important',
        maxWidth: 'none !important',
        color: '#475467 !important',
        fontSize: '14px !important',
        lineHeight: '22px !important',
      },
      '.bsv-report-export [data-total-impact-details="true"] [data-total-impact-item="true"]': {
        borderBottom: '1px solid #D1D5DB !important',
        padding: '5px 0 !important',
      },
      '.bsv-report-export [data-total-impact-details="true"] [data-total-impact-item="true"][data-total-impact-active="true"]': {
        paddingBottom: '8px !important',
      },
      '.bsv-report-export [data-total-impact-details="true"] [data-total-impact-heading="true"]': {
        fontWeight: '700 !important',
        color: '#0F172A !important',
        fontSize: '17px !important',
      },
      '.bsv-report-export [data-total-impact-details="true"] [data-total-impact-body="true"]': {
        display: 'block !important',
        color: '#334155 !important',
        fontSize: '14px !important',
        lineHeight: '20px !important',
        marginTop: '4px !important',
      },
      '.bsv-report-export [data-report-page="true"]:not(.coverPage) h1, .bsv-report-export [data-report-page="true"]:not(.coverPage) h2, .bsv-report-export [data-report-page="true"]:not(.coverPage) h3, .bsv-report-export [data-report-page="true"]:not(.coverPage) p, .bsv-report-export [data-report-page="true"]:not(.coverPage) span, .bsv-report-export [data-report-page="true"]:not(.coverPage) li': {
        color: '#111827 !important',
      },
      '.bsv-report-export iframe': {
        display: 'none !important',
      },
      '[data-report-social-issues="true"] h3, [data-report-social-issues="true"] h5': {
        color: '#334155 !important',
      },
      '.bsv-report-export [data-report-social-issues="true"]': {
        rowGap: '0 !important',
        columnGap: '48px !important',
      },
    },
  },
}));

class ReportErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Unknown report rendering error' };
  }

  componentDidCatch(error) {
    const message = error?.message || 'Unknown report rendering error';
    window.__REPORT__ = { status: 'error', errorMessage: message, ts: Date.now() };
    window.__PDF_READY__ = false;
    window.__PDF_ERROR__ = true;
    window.__PDF_ERROR_MESSAGE__ = message;
  }

  render() {
    if (this.state.hasError) {
      return <Typography color="error">{this.state.message}</Typography>;
    }

    return this.props.children;
  }
}

const MoodBoardExportPage = ({ moodBoard, campaignTitle, pillarTitles = [] }) => {
  if (!moodBoard || moodBoard?.status !== 'completed') return null;

  const getMoodTile = key => moodBoard?.tiles?.[key]?.url || '';
  const configuredPillarIndices = [1, 2, 3].filter(idx => Boolean(moodBoard?.summaries?.[`pillar${idx}_title`] || pillarTitles[idx - 1]));
  const displayPillars = configuredPillarIndices.length ? configuredPillarIndices : [1];
  const pillarCount = displayPillars.length;
  const topRowTemplate = '40% 60%';
  const pillarTitleLengths = displayPillars.map(idx => `${moodBoard?.summaries?.[`pillar${idx}_title`] || pillarTitles[idx - 1] || ''}`.length);
  const pillarSummaryLengths = displayPillars.map(idx => `${moodBoard?.summaries?.[`pillar${idx}_summary_50`] || ''}`.length);
  const longestTitleLength = Math.max(0, ...pillarTitleLengths);
  const longestSummaryLength = Math.max(0, ...pillarSummaryLengths);
  const mainImageStyleByPillarCount =
    pillarCount === 2
      ? { height: 220 }
      : pillarCount === 3
        ? { aspectRatio: '1 / 1' }
        : { aspectRatio: '1 / 1' };
  const supportingImageStyleByPillarCount =
    pillarCount === 2
      ? { height: 220 }
      : pillarCount === 3
        ? { aspectRatio: '1 / 1' }
        : { aspectRatio: '1 / 1' };
  const pillarTitleTextStyle =
    pillarCount > 1
      ? {
          minHeight: pillarCount === 3 ? (longestTitleLength > 44 ? 54 : 44) : (longestTitleLength > 52 ? 48 : 40),
          overflow: 'hidden',
        }
      : { minHeight: 38, overflow: 'hidden' };
  const pillarSummaryTextStyle =
    pillarCount > 1
      ? {
          minHeight:
            pillarCount === 3
              ? longestSummaryLength > 170
                ? 108
                : longestSummaryLength > 145
                  ? 96
                  : longestSummaryLength > 120
                    ? 84
                    : 72
              : longestSummaryLength > 165
                ? 92
                : longestSummaryLength > 135
                  ? 78
                  : 62,
          overflow: 'hidden',
        }
      : { minHeight: 56, overflow: 'hidden' };

  const renderMoodImage = (src, alt, style = {}) =>
    src ? (
      <img
        src={src}
        alt={alt}
        loading="eager"
        style={{ width: '100%', borderRadius: 5, objectFit: 'cover', display: 'block', ...style }}
      />
    ) : (
      <Box style={{ borderRadius: 5, border: '1px dashed #D1D5DB', ...style }} />
    );

  return (
    <>
      <Box
        className="reportPage"
        data-report-page="true"
        style={{
          width: 'var(--report-page-w)',
          minWidth: 'var(--report-page-w)',
          maxWidth: 'var(--report-page-w)',
          boxSizing: 'border-box',
          overflow: 'hidden',
          breakBefore: 'page',
          pageBreakBefore: 'always',
          background: '#fff',
          padding: '0.12in 0.18in 0.12in',
          color: '#111827',
        }}>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Box style={{ display: 'grid', gridTemplateColumns: topRowTemplate, gap: 8 }}>
            <Box style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: 8 }}>
              <Typography style={{ fontSize: 18, lineHeight: 1.2, fontWeight: 700, marginBottom: 6 }}>{campaignTitle || 'Campaign'}</Typography>
              <Typography style={{ fontSize: 10.5, lineHeight: 1.3 }}>{moodBoard?.summaries?.campaign_summary_100 || ''}</Typography>
            </Box>
            {renderMoodImage(getMoodTile('campaign_main'), 'Campaign main', { aspectRatio: '3 / 2', border: '1px solid #E5E7EB' })}
          </Box>

          <Box style={{ display: 'grid', gridTemplateColumns: `repeat(${pillarCount}, 1fr)`, gap: 7 }}>
            {displayPillars.map(idx => {
              const topImg = getMoodTile(`pillar${idx}_main`) || getMoodTile(`pillar${idx}_variant_extra`) || getMoodTile(`pillar1_variant_${idx}`);
              const bottomImg = getMoodTile(`pillar${idx}_supporting`) || getMoodTile(`pillar1_variant_${idx + 1}`);
              return (
                <Box key={`mood-pillar-${idx}`} style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: 7, display: 'flex', flexDirection: 'column' }}>
                  <Typography style={{ fontSize: 12.5, lineHeight: 1.2, fontWeight: 700, marginBottom: 2, ...pillarTitleTextStyle }}>
                    {`Pillar ${idx}: ${moodBoard?.summaries?.[`pillar${idx}_title`] || pillarTitles[idx - 1] || '...'}`}
                  </Typography>
                  <Typography style={{ fontSize: 9.5, lineHeight: 1.25, marginBottom: 6, ...pillarSummaryTextStyle }}>
                    {moodBoard?.summaries?.[`pillar${idx}_summary_50`] || ''}
                  </Typography>
                  {pillarCount === 1 ? (
                    <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 4 }}>
                      {renderMoodImage(topImg, `Pillar ${idx} main`, mainImageStyleByPillarCount)}
                      {renderMoodImage(bottomImg, `Pillar ${idx} supporting`, supportingImageStyleByPillarCount)}
                    </Box>
                  ) : (
                    <Box style={{ marginTop: pillarCount > 1 ? 8 : 5 }}>
                      {renderMoodImage(topImg, `Pillar ${idx} main`, mainImageStyleByPillarCount)}
                      {renderMoodImage(bottomImg, `Pillar ${idx} supporting`, { ...supportingImageStyleByPillarCount, marginTop: 8 })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box
        className="reportPage"
        data-report-page="true"
        style={{
          width: 'var(--report-page-w)',
          minWidth: 'var(--report-page-w)',
          maxWidth: 'var(--report-page-w)',
          boxSizing: 'border-box',
          overflow: 'hidden',
          breakBefore: 'page',
          pageBreakBefore: 'always',
          background: '#fff',
          padding: '0.12in 0.18in 0.12in',
          color: '#111827',
        }}>
        <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {[{ key: 'palette_tile', label: 'Palette' }, { key: 'texture_tile', label: 'Textures' }, { key: 'typography_final', label: 'Typography' }].map(item => (
            <Box key={item.key} style={{ border: '1px solid #E5E7EB', borderRadius: 6, padding: 8 }}>
              <Typography style={{ fontSize: 14, lineHeight: 1.2, fontWeight: 700, marginBottom: 5 }}>{item.label}</Typography>
              {renderMoodImage(getMoodTile(item.key), item.label, { aspectRatio: '2 / 3' })}
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
};

const ReportLayout = ({ title, metadata = [], children, ready, hasError = false, errorMessage = '', coverType, moodBoardExport }) => {
  const classes = useStyles();
  const location = useLocation();
  const isReportExport = location.pathname.includes('/report-export/');
  const searchParams = new URLSearchParams(window.location.search);
  const paper = searchParams.get('paper');
  const paperW = searchParams.get('paperW');
  const paperH = searchParams.get('paperH');
  const paperMargin = searchParams.get('paperM');

  const paperWidth = paperW || (paper === 'A4' ? '8.27in' : '8.5in');
  const paperHeight = paperH || (paper === 'A4' ? '11.69in' : '11in');
  const margin = paperMargin || '0.5in';

  useEffect(() => {
    console.log('REPORT_BUNDLE_LOADED');
  }, []);

  useEffect(() => {
    let cancelled = false;

    const commitState = state => {
      window.__REPORT__ = state;
      window.__PDF_READY__ = state.status === 'ready';
      window.__PDF_ERROR__ = state.status === 'error';
      window.__PDF_ERROR_MESSAGE__ = state.errorMessage;
    };

    const waitForFrame = () =>
      new Promise(resolve => {
        window.requestAnimationFrame(() => resolve());
      });

    const setReadyState = async () => {
      const reportState = {
        status: 'loading',
        errorMessage: '',
        ts: Date.now(),
      };

      if (!ready) {
        commitState(reportState);
        return;
      }

      if (hasError) {
        reportState.status = 'error';
        reportState.errorMessage = errorMessage || 'Unknown report error';
        commitState(reportState);
        return;
      }

      if (isReportExport) {
        if (document?.fonts?.ready) {
          await document.fonts.ready;
        }
        const images = Array.from(document.querySelectorAll('#report-root img'));
        await Promise.all(
          images.map(
            img =>
              new Promise(resolve => {
                if (img.complete) return resolve();
                img.onload = () => resolve();
                img.onerror = () => resolve();
              })
          )
        );
        await waitForFrame();
        await waitForFrame();
        if (typeof window.__PDF_RESIZE_CHARTS__ === 'function') {
          window.__PDF_RESIZE_CHARTS__();
        }
        await waitForFrame();
      }

      if (cancelled) return;
      reportState.status = 'ready';
      commitState(reportState);
    };

    setReadyState();

    return () => {
      cancelled = true;
    };
  }, [ready, hasError, errorMessage, isReportExport]);

  return (
    <Box className={`${classes.page} ${isReportExport ? 'bsv-report-export' : ''}`}>
      <Box
        className={classes.exportRoot}
        data-report-root="true"
        id="report-root"
        style={{
          '--paper-w': paperWidth,
          '--paper-h': paperHeight,
          '--paper-m': margin,
          '--report-page-w': `calc(${paperWidth} - (2 * ${margin}))`,
          '--report-page-h': `calc(${paperHeight} - (2 * ${margin}))`,
        }}>
        {coverType === 'marketing-strategy' && isReportExport ? (
          <CoverPageMarketingStrategy title={title} metadata={metadata} />
        ) : null}
        <Box className={`${classes.reportSection} ${classes.reportPage}`} data-report-page="true">
          <Box className={classes.section}>
            <ReportErrorBoundary>{children}</ReportErrorBoundary>
          </Box>
        </Box>
        <MoodBoardExportPage
          moodBoard={moodBoardExport?.moodBoard}
          campaignTitle={moodBoardExport?.campaignTitle}
          pillarTitles={moodBoardExport?.pillarTitles || []}
        />
      </Box>
    </Box>
  );
};

export default ReportLayout;
